import React, { createContext, useContext } from 'react';
import { string, node } from 'prop-types';

/**
 * Nike AI Variant Context
 * Provides the active Nike AI variant to child components
 * Used to conditionally render Nike AI entry points based on experiment
 *
 * Values:
 * - null: Control group (no Nike AI)
 * - 'variant-A': Header icon entry point
 * - 'variant-B': FAB + search preview entry points
 *
 * ⚠️ DISCUSSION POINT FOR PR:
 * Alternative approach (consistent with existing experiments like global_nav_favorites):
 * - Remove this context provider entirely
 * - Call useExperiment([{ name: 'search_digital_coach' }]) directly in:
 *   - Header component (for icon in UserTools)
 *   - Footer component (for lazy loading variants)
 *   - Pass variant via props to UserTools and VisualSearchResults
 *
 * Context approach pros:
 * - Cleaner DX: Components consume via hook instead of prop drilling
 * - Could standardize for future experiments (avoid multiple useExperiment calls)
 * - Single source of truth per entry point (header vs footer)
 *
 * Context approach cons:
 * - Adds Provider wrapper to control group (though minimal impact)
 * - Different pattern from existing experiments (inconsistent)
 * - Extra infrastructure for one experiment
 *
 * Question for PR: Should we adopt context pattern for ALL future experiments?
 */
const NikeAIVariantContext = createContext(null);

export const NikeAIVariantProvider = ({ children, variant }) => {
  return <NikeAIVariantContext.Provider value={variant}>{children}</NikeAIVariantContext.Provider>;
};

NikeAIVariantProvider.propTypes = {
  children: node.isRequired,
  variant: string, // null, 'variant-A', or 'variant-B'
};

/**
 * Hook to access the current Nike AI variant
 * @returns {string|null} Current variant or null for control
 */
export const useNikeAIVariant = () => {
  return useContext(NikeAIVariantContext);
};
